import { useCallback, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { compareFileExtension } from 'utils/helpers'
import { Button } from 'components/common'
import { ImportIcon } from 'components/icons'
import ImportModal, { ImportColumn } from 'components/common/importModal'
import '../fileUploadButton/fileUpload.css'

type Props = {
  supportedFileTypes?: string
  onFileSelected: (file: File | null) => void
  disabled?: boolean
  importColumns?: ImportColumn[]
  importModuleName?: string
  importSampleFileUrl?: string
  onDownloadSample?: () => Promise<void>
}

/**
 * Import Barcode CSV component - Only handles file selection, not uploading
 */
const BarcodeImportButton = ({
  supportedFileTypes = '.csv',
  onFileSelected,
  disabled = false,
  importColumns,
  importModuleName = '',
  importSampleFileUrl,
  onDownloadSample,
}: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Handle file selection (not uploading)
  const handleFileChange = useCallback(() => {
    // Check if file exists
    if (!inputRef?.current?.files || inputRef?.current?.files?.length === 0) {
      return
    }

    const file = inputRef?.current?.files[0]

    // Check file extension
    if (!compareFileExtension(file, 'csv')) {
      toast.error('Invalid file format. Only CSV files are allowed.', {
        duration: 5000,
      })
      return resetFileSelection()
    }

    // Set the selected file and notify parent
    setSelectedFile(file)
    onFileSelected(file)
  }, [inputRef, onFileSelected])

  // Reset the file selection
  const resetFileSelection = useCallback(() => {
    if (inputRef.current) inputRef.current.value = ''
    setSelectedFile(null)
    onFileSelected(null)
  }, [inputRef, onFileSelected])

  const triggerFilePicker = () => {
    inputRef.current?.click()
  }

  // Opens import info modal first if columns are configured, otherwise opens file picker directly
  const handleSelectFile = () => {
    if (importColumns && importColumns.length > 0) {
      setShowImportModal(true)
    } else {
      triggerFilePicker()
    }
  }

  const handleImportModalConfirm = () => {
    setShowImportModal(false)
    triggerFilePicker()
  }

  return (
    <div className="flex gap-5 ">
      <input
        className="hidden"
        type="file"
        onChange={handleFileChange}
        accept={supportedFileTypes}
        ref={inputRef}
        disabled={disabled}
      />

      <Button
        buttonText={
          selectedFile ? `Selected: ${selectedFile.name}` : 'Select Barcode CSV'
        }
        onClick={handleSelectFile}
        variant="custom"
        className={`rounded-lg bg-gray-100 px-3.5 py-2 text-sm font-semibold ${
          selectedFile
            ? 'bg-green-100 text-green-800 hover:bg-green-200'
            : 'text-sidebar-primary hover:bg-gray-200'
        }`}
        icon={<ImportIcon />}
        iconClassName="!h-5 !w-5 text-dataTableIcon"
        disabled={disabled}
        type="button"
      />

      {selectedFile && (
        <Button
          buttonText="Clear"
          onClick={resetFileSelection}
          variant="custom"
          className="rounded-lg px-3.5 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
          type="button"
        />
      )}
      {importColumns && importColumns.length > 0 && (
        <ImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onConfirm={handleImportModalConfirm}
          moduleName={importModuleName}
          columns={importColumns}
          sampleFileUrl={importSampleFileUrl}
          onDownloadSample={onDownloadSample}
        />
      )}
    </div>
  )
}

export default BarcodeImportButton
